import type { AccessOverviewModel } from '.';
import type { AccessByType, AccessEntry, AccessMap, FlatAccessEntry, UserNode } from './graph';
import { buildAccessMap, buildFlatAccessList, enrichGroupEntries } from './graph';
import {
    accessOverviewReducer,
    dataError,
    dataReady,
    initialAccessOverviewState,
    loadUser,
    selectNode,
    setActiveTab,
    setSearchQuery,
} from './state';
import type { AccessOverviewAction, AccessOverviewState, AccessOverviewTab } from './state';
import { computed, effect, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryClient, QueryObserverOptions } from '@tanstack/query-core';
import type { Group } from '@thinkalpha/access-ws-client/contracts/group.js';
import type { DetailedUser, SecurityPackage } from '@thinkalpha/access-ws-client/contracts/user.js';
import type { ConcreteAccount } from '@thinkalpha/trading-ws-client/contracts/account.js';
import type { AggregatedRole } from 'src/admin/features/AccessOverview/helpers';
import { aggregateRoles } from 'src/admin/features/AccessOverview/helpers';
import { inject, injectable } from 'src/features/ioc';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getAccountsForSubjectQuery, getFirmMemberAccountsQuery } from 'src/queries/accounts';
import { getGroupsByIdQuery } from 'src/queries/group';
import { getFirmMemberSecurityPackageQuery, getUserQuery, getUserSecurityPackageQuery } from 'src/queries/user';
import type { ReactBindings } from 'src/types/bindings';
import type { z } from 'zod';

type SecurityPackageData = z.infer<typeof SecurityPackage>;

@injectable()
export class AccessOverviewModelImpl implements AccessOverviewModel {
    readonly #disposableStack = new DisposableStack();

    // --- Internal state ---
    readonly #state = signal<AccessOverviewState>(initialAccessOverviewState);

    #dispatch(action: AccessOverviewAction): void {
        this.#state.value = accessOverviewReducer(this.#state.value, action);
    }

    // --- Query observers ---
    #userQuery: QueryObserverModel<DetailedUser, Error, DetailedUser>;
    #securityPackageQuery: QueryObserverModel<SecurityPackageData, Error, SecurityPackageData>;
    #groupsQuery: QueryObserverModel<Group[], Error, Group[]>;
    #accountsQuery: QueryObserverModel<ConcreteAccount[], Error, ConcreteAccount[]>;

    // Internal signal for group IDs (updated when user resolves)
    readonly #groupIds = signal<string[] | null>(null);

    // --- State-derived signals ---
    readonly phase: ReadonlySignal<AccessOverviewState['phase']> = computed(() => this.#state.value.phase);
    readonly error: ReadonlySignal<string | null> = computed(() => this.#state.value.error);
    readonly selectedNodeId: ReadonlySignal<string | null> = computed(() => this.#state.value.selectedNodeId);
    readonly searchQuery: ReadonlySignal<string> = computed(() => this.#state.value.searchQuery);
    readonly activeTab: ReadonlySignal<AccessOverviewTab> = computed(() => this.#state.value.activeTab);

    // --- Query-derived signals ---
    readonly #user: ReadonlySignal<DetailedUser | null>;
    readonly #securityPackage: ReadonlySignal<SecurityPackageData | null>;
    readonly #groups: ReadonlySignal<Group[]>;
    readonly #accounts: ReadonlySignal<ConcreteAccount[]>;

    readonly isLoading: ReadonlySignal<boolean>;

    // --- Computed data signals ---
    readonly graph: ReadonlySignal<AccessMap | null>;
    readonly accessByType: ReadonlySignal<AccessByType | null>;
    readonly flatAccessList: ReadonlySignal<FlatAccessEntry[]>;
    readonly userNode: ReadonlySignal<(AccessEntry & UserNode) | null>;
    readonly selectedNode: ReadonlySignal<AccessEntry | null>;

    // --- Derived UI signals ---
    readonly filteredRoles: ReadonlySignal<AggregatedRole[]>;
    readonly groupedRoles: ReadonlySignal<[string, AggregatedRole[]][]>;

    constructor(
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('QueryClient') private queryClient: QueryClient,
    ) {
        const userId = computed(() => this.#state.value.userId);
        const firmId = computed(() => this.#state.value.firmId);

        // --- Initialize query observers ---
        // getUserQuery is typed for ui-types/DetailedUser, but the API returns the full
        // access-ws-client/DetailedUser shape at runtime. We cast the options to align
        // with the access-ws-client type used by the graph builder.
        this.#userQuery = queryObserverModelFactory<DetailedUser, Error, DetailedUser>(
            computed(
                () =>
                    getUserQuery(userId.value ?? undefined) as unknown as QueryObserverOptions<
                        DetailedUser,
                        Error,
                        DetailedUser
                    >,
            ),
        );
        this.#disposableStack.use(this.#userQuery);

        this.#securityPackageQuery = queryObserverModelFactory<SecurityPackageData, Error, SecurityPackageData>(
            computed(() =>
                firmId.value
                    ? getFirmMemberSecurityPackageQuery(firmId.value, userId.value ?? undefined)
                    : getUserSecurityPackageQuery(userId.value ?? undefined),
            ),
        );
        this.#disposableStack.use(this.#securityPackageQuery);

        this.#groupsQuery = queryObserverModelFactory<Group[], Error, Group[]>(
            computed(
                () =>
                    getGroupsByIdQuery(this.#groupIds.value, this.queryClient) as unknown as QueryObserverOptions<
                        Group[],
                        Error,
                        Group[]
                    >,
            ),
        );
        this.#disposableStack.use(this.#groupsQuery);

        this.#accountsQuery = queryObserverModelFactory<ConcreteAccount[], Error, ConcreteAccount[]>(
            computed(
                () =>
                    (firmId.value
                        ? getFirmMemberAccountsQuery(firmId.value, userId.value ?? undefined)
                        : getAccountsForSubjectQuery(
                              userId.value ?? undefined,
                              'user',
                          )) as unknown as QueryObserverOptions<ConcreteAccount[], Error, ConcreteAccount[]>,
            ),
        );
        this.#disposableStack.use(this.#accountsQuery);

        // --- Derive data from queries ---
        this.#user = computed(() => this.#userQuery.signal.value.data ?? null);
        this.#securityPackage = computed(() => this.#securityPackageQuery.signal.value.data ?? null);
        this.#groups = computed(() => this.#groupsQuery.signal.value.data ?? []);
        this.#accounts = computed(() => this.#accountsQuery.signal.value.data ?? []);

        this.isLoading = computed(() => {
            if (this.#state.value.phase !== 'loading') return false;
            return (
                this.#userQuery.signal.value.isLoading ||
                this.#securityPackageQuery.signal.value.isLoading ||
                this.#groupsQuery.signal.value.isLoading ||
                this.#accountsQuery.signal.value.isLoading
            );
        });

        // --- Build graph when all data is available ---
        this.graph = computed(() => {
            const user = this.#user.value;
            const securityPackage = this.#securityPackage.value;
            if (!user || !securityPackage) return null;

            const groups = this.#groups.value;
            const accounts = this.#accounts.value;

            const scopedFirmId = this.#state.value.firmId;
            let firmsForGraph = user.firms;
            let userGroupsForGraph = user.groups;
            if (scopedFirmId) {
                firmsForGraph = user.firms.filter((f) => f.firmId === scopedFirmId);
                const inFirmGroupIds = new Set(groups.filter((g) => g.firmId === scopedFirmId).map((g) => g.id));
                userGroupsForGraph = user.groups.filter((g) => inFirmGroupIds.has(g.groupId));
            }

            const map = buildAccessMap(user, securityPackage, firmsForGraph, userGroupsForGraph, accounts);
            enrichGroupEntries(map, groups);
            return map;
        });

        this.flatAccessList = computed(() => {
            const graph = this.graph.value;
            const securityPackage = this.#securityPackage.value;
            if (!graph || !securityPackage) return [];
            return buildFlatAccessList(graph, securityPackage);
        });

        this.accessByType = computed(() => {
            const entries = this.flatAccessList.value;
            if (entries.length === 0 && !this.graph.value) return null;
            return categorizeByType(entries);
        });

        this.userNode = computed(() => {
            const graph = this.graph.value;
            if (!graph) return null;
            for (const entry of graph.values()) {
                if (entry.type === 'user') return entry as AccessEntry & UserNode;
            }
            return null;
        });

        this.selectedNode = computed(() => {
            const graph = this.graph.value;
            const nodeId = this.selectedNodeId.value;
            if (!graph || !nodeId) return null;
            return graph.get(nodeId) ?? null;
        });

        // --- Derived UI signals ---
        const allRoles = computed(() => {
            const graph = this.graph.value;
            if (!graph) return [];
            return aggregateRoles(graph);
        });

        this.filteredRoles = computed(() => {
            const roles = allRoles.value;
            const query = this.searchQuery.value.trim().toLowerCase();
            if (!query) return roles;
            return roles.filter(
                (r) =>
                    r.role.toLowerCase().includes(query) ||
                    r.sources.some(
                        (s) => s.nodeLabel.toLowerCase().includes(query) || s.nodeType.toLowerCase().includes(query),
                    ),
            );
        });

        this.groupedRoles = computed(() => {
            const roles = this.filteredRoles.value;
            const groups = new Map<string, AggregatedRole[]>();
            for (const role of roles) {
                const ns = role.role.includes('::') ? role.role.split('::')[0] : 'other';
                let list = groups.get(ns);
                if (!list) {
                    list = [];
                    groups.set(ns, list);
                }
                list.push(role);
            }
            return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
        });

        // --- Effects ---

        // When user resolves, extract all group IDs for the groups query
        this.#disposableStack.defer(
            effect(() => {
                const user = this.#user.value;
                if (!user) return;

                untracked(() => {
                    const groupIds = new Set<string>();
                    for (const g of user.groups) {
                        groupIds.add(g.groupId);
                    }
                    for (const firm of user.firms) {
                        if (firm.groups) {
                            for (const g of firm.groups) {
                                groupIds.add(g.groupId);
                            }
                        }
                        if (firm.appliedGroups) {
                            for (const g of firm.appliedGroups) {
                                groupIds.add(g.groupId);
                            }
                        }
                    }
                    this.#groupIds.value = groupIds.size > 0 ? [...groupIds] : null;
                });
            }),
        );

        // Track phase transitions based on query states
        this.#disposableStack.defer(
            effect(() => {
                const userError = this.#userQuery.signal.value.error;
                const secPkgError = this.#securityPackageQuery.signal.value.error;
                const groupsError = this.#groupsQuery.signal.value.error;
                const accountsError = this.#accountsQuery.signal.value.error;

                const userLoading = this.#userQuery.signal.value.isLoading;
                const secPkgLoading = this.#securityPackageQuery.signal.value.isLoading;
                const groupsLoading = this.#groupsQuery.signal.value.isLoading;
                const accountsLoading = this.#accountsQuery.signal.value.isLoading;

                const graph = this.graph.value;

                untracked(() => {
                    const { phase } = this.#state.value;
                    if (phase !== 'loading') return;

                    const firstError = userError ?? secPkgError ?? groupsError ?? accountsError;
                    if (firstError) {
                        this.#dispatch(dataError(firstError.message));
                        return;
                    }

                    if (!userLoading && !secPkgLoading && !groupsLoading && !accountsLoading && graph) {
                        this.#dispatch(dataReady());
                    }
                });
            }),
        );
    }

    // --- Public methods ---

    loadUser(userId: string, firmId: string | null = null): void {
        // Only clear when the user actually changes; otherwise the user-resolved
        // effect that repopulates groupIds won't refire (same query key) and
        // group enrichment would be lost on a firm-scope swap.
        if (this.#state.value.userId !== userId) this.#groupIds.value = null;
        this.#dispatch(loadUser(userId, firmId));
    }

    selectNode(nodeId: string | null): void {
        this.#dispatch(selectNode(nodeId));
    }

    toggleNode(nodeId: string): void {
        const current = this.#state.value.selectedNodeId;
        this.#dispatch(selectNode(current === nodeId ? null : nodeId));
    }

    setSearchQuery(query: string): void {
        this.#dispatch(setSearchQuery(query));
    }

    setActiveTab(tab: AccessOverviewTab): void {
        this.#dispatch(setActiveTab(tab));
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}

// --- Helper ---

function categorizeByType(entries: FlatAccessEntry[]): AccessByType {
    const result: AccessByType = {
        firms: [],
        groups: [],
        subscriptions: [],
        routes: [],
        accounts: [],
        workspaces: [],
        ideas: [],
        indicators: [],
        columnTemplates: [],
    };

    for (const entry of entries) {
        const { node } = entry;
        switch (node.type) {
            case 'firm':
                result.firms.push(entry);
                break;
            case 'group':
                result.groups.push(entry);
                break;
            case 'subscription':
                result.subscriptions.push(entry);
                break;
            case 'route':
                // Safe: node.type === 'route' guarantees RouteNode
                result.routes.push(entry as (typeof result.routes)[number]);
                break;
            case 'asset': {
                // TypeScript can't fully narrow AssetNodeOf<T> through the nested switch on
                // meta.assetType, so cast only within each checked branch to the exact target
                // entry type instead of erasing type-safety with `any`.
                switch (node.meta.assetType) {
                    case 'account':
                        result.accounts.push(entry as (typeof result.accounts)[number]);
                        break;
                    case 'workspace':
                        result.workspaces.push(entry as (typeof result.workspaces)[number]);
                        break;
                    case 'idea':
                        result.ideas.push(entry as (typeof result.ideas)[number]);
                        break;
                    case 'indicator':
                        result.indicators.push(entry as (typeof result.indicators)[number]);
                        break;
                    case 'column-template':
                        result.columnTemplates.push(entry as (typeof result.columnTemplates)[number]);
                        break;
                }
                break;
            }
        }
    }

    return result;
}
